import React from 'react';
import { HomeIcon, MapPinIcon } from 'lucide-react';
import { PageHeader } from '../components/layout/PageHeader';
import { CtaBand } from '../components/ui/CtaBand';
import { SectionHeading } from '../components/ui/SectionHeading';
import { labPanels } from '../data/site';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
const markerGroups = [{
  group: 'Hormones',
  markers: ['Total testosterone', 'Free testosterone', 'Estradiol', 'SHBG', 'LH', 'FSH', 'DHEA-S', 'Progesterone']
}, {
  group: 'Metabolic',
  markers: ['HbA1c', 'Fasting glucose', 'Fasting insulin', 'ApoB', 'Lp(a)', 'Full lipid panel', 'Uric acid']
}, {
  group: 'Thyroid & inflammation',
  markers: ['TSH', 'Free T3', 'Free T4', 'TPO antibodies', 'hs-CRP', 'Homocysteine', 'Ferritin']
}, {
  group: 'Organ & blood',
  markers: ['CBC with differential', 'CMP', 'eGFR', 'ALT / AST', 'PSA (men)', 'Vitamin D', 'B12']
}];
export function Labs() {
  useDocumentMeta({
    title: 'Labs & testing — Republic',
    description: 'A 62-marker baseline panel included with every membership, collected at home or at 2,100 partner draw sites.'
  });
  return <main data-id="element-398" data-mp="x7rw06xuqbq4">
      <PageHeader eyebrow="Labs & testing" title="The panel is the foundation, not an upsell." intro="Republic includes a baseline panel with every membership and annotates the results in plain language before any treatment decision is made." data-id="element-399" data-mp="x80bm6lczv50" />

      <section className="bg-cream" data-id="element-400" data-mp="4lfj32t5dczv">
        <div className="mx-auto max-w-shell px-6 pb-20 pt-10 md:px-8 md:pb-28" data-id="element-401" data-mp="9he2ebzmc4tf">
          <img src="https://cdn.magicpatterns.com/patterns/generated-images/e729c5dc-755b-4a47-bb49-2404bfa42c4e.jpg" alt="A Republic at-home collection kit laid out on a black slate surface" className="aspect-[16/7] w-full object-cover" data-id="element-402" data-mp="ehiku7k379ma" />

          <ul className="mt-14 grid gap-8 lg:grid-cols-3" data-id="element-403" data-mp="1ty0vbr6vflfg">
            {labPanels.map(panel => <li key={panel.name} className="rule pt-6" data-id="element-404" data-mp="3qyn6chjd2lbn">
                <div className="flex items-baseline justify-between gap-4" data-id="element-405" data-mp="3qx3sexl6gmd1">
                  <h2 className="font-serif text-2xl text-ink" data-id="element-406" data-mp="3lyiq8smd7sly">{panel.name}</h2>
                  <span className="font-serif text-3xl text-brick" data-id="element-407" data-mp="3uh561ve7dfe">{panel.markers}</span>
                </div>
                <p className="mt-3 text-base leading-relaxed text-ink-soft" data-id="element-408" data-mp="3v00gh3thlaod">{panel.detail}</p>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="bg-cream-light" data-id="element-409" data-mp="3392pc46rpy50">
        <div className="mx-auto max-w-shell px-6 py-20 md:px-8 md:py-28" data-id="element-410" data-mp="33a434gf64yqf">
          <SectionHeading eyebrow="What we measure" title="Every marker, published before you book." intro="No panel at Republic contains a marker we cannot explain the clinical reason for." data-id="element-411" data-mp="3h10rfv22atro" />
          <div className="mt-14 grid gap-10 md:grid-cols-2 lg:grid-cols-4" data-id="element-412" data-mp="2oiloon1yjc96">
            {markerGroups.map(group => <div key={group.group} data-id="element-413" data-mp="2jj0l1zbuefq4">
                <h3 className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-414" data-mp="z2j51c1r4rro">
                  {group.group}
                </h3>
                <ul className="mt-5" data-id="element-415" data-mp="13v2ebwtep0qo">
                  {group.markers.map(marker => <li key={marker} className="rule py-3 text-sm text-ink-soft" data-id="element-416" data-mp="q3xp7onnzqul">
                      {marker}
                    </li>)}
                </ul>
              </div>)}
          </div>
        </div>
      </section>

      <section className="bg-cream" data-id="element-417" data-mp="1mt59yugglvxg">
        <div className="mx-auto max-w-shell px-6 py-20 md:px-8 md:py-24" data-id="element-418" data-mp="1ms3m1pecheyb">
          <SectionHeading eyebrow="Collection" title="Two ways to get your blood drawn." data-id="element-419" data-mp="18tnfpzke9qgp" />
          <div className="mt-12 grid gap-8 md:grid-cols-2" data-id="element-420" data-mp="1drfglj30omx1">
            {[{
            icon: HomeIcon,
            title: 'At home',
            detail: 'A kit ships within one business day. Collect in ten minutes and return it in the prepaid mailer. Results in three to five days.',
            meta: 'Included with membership'
          }, {
            icon: MapPinIcon,
            title: 'At a partner site',
            detail: 'Book a draw at one of 2,100 partner locations, usually same week. Fasting appointments available before 10am.',
            meta: 'Included with membership'
          }].map(option => <div key={option.title} className="border border-ink/15 bg-cream-light p-8" data-id="element-421" data-mp="10khxrjvlju8b">
                <option.icon className="h-6 w-6 text-ink" strokeWidth={1.4} data-id="element-422" data-mp="wkh9z8mly6ol" />
                <h3 className="mt-6 font-serif text-2xl text-ink" data-id="element-423" data-mp="rjjpuqi1f2kg">{option.title}</h3>
                <p className="mt-3 text-base leading-relaxed text-ink-soft" data-id="element-424" data-mp="mmb7aosz8wio">{option.detail}</p>
                <p className="mt-6 text-[0.6875rem] uppercase tracking-label text-brick" data-id="element-425" data-mp="3cb3twu11p2n1">
                  {option.meta}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      <CtaBand title="Get the numbers first." intro="Panels are available without a membership if you would rather see your data before deciding on treatment." data-id="element-426" data-mp="2x952s3m2su82" />
    </main>;
}